/**
 * Centralized search configuration for consistent behavior across the app
 * Adjust these values to make search more or less aggressive
 *
 * Current search scope:
 * - Users: Username only (no name matching)
 * - Festivals: Name and alias only (no location/date matching)
 */

export const SEARCH_CONFIG = {
	// Minimum characters required before search starts
	MIN_QUERY_LENGTH: 2,

	// Debounce time in milliseconds (higher = less aggressive)
	DEBOUNCE_MS: 400, // Increased from 300ms to be less aggressive

	// Default result limits
	DEFAULT_LIMIT: 5,
	MAX_LIMIT: 20,

	// Cache settings
	STALE_TIME: 5 * 60 * 1000, // 5 minutes
	GC_TIME: 10 * 60 * 1000, // 10 minutes
} as const;

/**
 * Get search options with sensible defaults
 */
export function getSearchOptions(overrides?: {
	minQueryLength?: number;
	debounceMs?: number;
	limit?: number;
	enabled?: boolean;
}) {
	return {
		minQueryLength: SEARCH_CONFIG.MIN_QUERY_LENGTH,
		debounceMs: SEARCH_CONFIG.DEBOUNCE_MS,
		limit: SEARCH_CONFIG.DEFAULT_LIMIT,
		enabled: true,
		...overrides,
	};
}

/**
 * Check if a query is valid for search
 */
export function isValidSearchQuery(
	query: string,
	minLength = SEARCH_CONFIG.MIN_QUERY_LENGTH,
): boolean {
	return query.trim().length >= minLength;
}

/**
 * Calculate search relevance score for client-side ranking
 * Higher score = more relevant
 */
export function calculateSearchRelevance(text: string, query: string): number {
	if (!text || !query) return 0;

	const textLower = text.toLowerCase();
	const queryLower = query.toLowerCase();

	// Exact match (highest score)
	if (textLower === queryLower) return 100;

	// Starts with query (high score)
	if (textLower.startsWith(queryLower)) return 80;

	// Contains query at word boundary (medium-high score)
	if (textLower.includes(` ${queryLower}`)) return 60;

	// Contains query anywhere (medium score)
	if (textLower.includes(queryLower)) return 40;

	// Fuzzy match using simple string distance (low score)
	const distance = getLevenshteinDistance(textLower, queryLower);
	const maxLength = Math.max(textLower.length, queryLower.length);
	const similarity = 1 - distance / maxLength;

	// Only return fuzzy matches if similarity is high enough
	return similarity > 0.7 ? similarity * 20 : 0;
}

/**
 * Simple Levenshtein distance calculation for fuzzy matching
 */
function getLevenshteinDistance(str1: string, str2: string): number {
	const matrix: number[][] = Array(str2.length + 1)
		.fill(null)
		.map(() => Array(str1.length + 1).fill(0));

	// Initialize first row and column
	for (let i = 0; i <= str1.length; i++) {
		matrix[0]![i] = i;
	}
	for (let j = 0; j <= str2.length; j++) {
		matrix[j]![0] = j;
	}

	// Fill the matrix
	for (let j = 1; j <= str2.length; j++) {
		for (let i = 1; i <= str1.length; i++) {
			const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
			const currentRow = matrix[j]!;
			const prevRow = matrix[j - 1]!;

			currentRow[i] = Math.min(
				currentRow[i - 1]! + 1, // deletion
				prevRow[i]! + 1, // insertion
				prevRow[i - 1]! + indicator, // substitution
			);
		}
	}

	return matrix[str2.length]?.[str1.length] ?? 0;
}

// Smart search configuration for natural language query understanding
// This enables searches like "armin 2025 asot" to find "Armin van Buuren at A State of Trance 2025"

export interface SearchToken {
	type:
		| "artist"
		| "festival"
		| "event_brand"
		| "year"
		| "genre"
		| "stage"
		| "unknown";
	value: string;
	confidence: number;
	originalTerm: string;
}

export interface ParsedQuery {
	tokens: SearchToken[];
	artistHints: string[];
	festivalHints: string[];
	yearHints: number[];
	genreHints: string[];
	stageHints: string[];
	originalQuery: string;
	searchStrategy: "exact" | "partial" | "fuzzy";
}

// Artist nickname/alias mapping for common abbreviations and shortened names
export const ARTIST_ALIASES: Record<string, string[]> = {
	// Electronic music superstars
	"Armin van Buuren": ["armin", "avb"],
	Tiësto: ["tiesto"],
	"Calvin Harris": ["calvin harris"],
	"David Guetta": ["guetta"],
	"Martin Garrix": ["garrix"],
	Deadmau5: ["deadmau5", "mau5", "joel zimmerman"],
	Skrillex: ["skrill", "sonny moore"],
	Diplo: ["diplo", "thomas wesley"],
	"Swedish House Mafia": ["shm", "swedish house"],

	// Trance legends
	"Above & Beyond": ["above beyond", "a&b"],
	"Paul van Dyk": ["pvd"],
	"Ferry Corsten": ["corsten"],
	"Markus Schulz": [],
	"Aly & Fila": ["aly fila", "a&f"],

	// Techno/House icons
	"Charlotte de Witte": ["cdw"],
	"Carl Cox": [],
	"Adam Beyer": [],
	"Nina Kraviz": [],
	"Amelie Lens": [],
	"Tale of Us": ["tou"],

	// Bass/Dubstep
	Illenium: [],
	"Seven Lions": ["7l"],
	Subtronics: [],
	Rezz: [],

	// Progressive/Melodic
	Pryda: ["pryda", "eric prydz"],
	"Eric Prydz": ["prydz", "pryda"],
	"Lane 8": ["lane8"],
	Yotto: [],

	// Collaborative aliases
	"HI-LO": ["hilo", "hi lo", "oliver heldens"],
	TESTPILOT: ["testpilot", "test pilot", "deadmau5"],
	VOYD: ["voyd", "rezz"],
};

// Festival/Event brand aliases and nicknames
export const FESTIVAL_ALIASES: Record<string, string[]> = {
	// Major festivals
	// Note: Removed "ultra" as an alias since it's part of actual festival names in DB
	"Ultra Music Festival": ["umf", "ultra miami"],
	Tomorrowland: ["tml", "boom"],
	"Electric Daisy Carnival": ["edc", "electric daisy"],
	Coachella: ["coach"],
	"Burning Man": ["burning man", "playa"],

	// Event brands/series
	"A State of Trance": ["asot", "state of trance"],
	Anjunabeats: ["anjunabeats"],
	Anjunadeep: ["anjuna deep", "anjunadeep"],
	ABGT: ["above beyond group therapy", "group therapy"],
	Dreamstate: ["dream state", "dreamstate"],
	"Factory 93": ["f93", "factory93"],
	Resistance: [],

	// Label showcases
	Armada: ["armada invites", "armada music"],
	"Spinnin Records": ["spinnin", "spinning"],
	Monstercat: ["monstercat"],
	OWSLA: ["owsla"],
};

// Stage name aliases
export const STAGE_ALIASES: Record<string, string[]> = {
	"Main Stage": ["mainstage"],
	"Circuit Grounds": ["circuit grounds"],
	"Worldwide Stage": ["worldwide stage"],
	"Quantum Valley": ["quantum valley"],
	"Neon Garden": ["neon garden"],
	Wasteland: ["wasteland"],
	"Freedom Stage": ["freedom stage"],
	Atmosphere: [],
	"Core Stage": ["core stage"],
	Mainstage: [],
	"Mythical Frames": ["mythical frames"],
	"The Boat": [],
	Elixir: [],
};

// Genre aliases for better matching
export const GENRE_ALIASES: Record<string, string[]> = {
	Trance: ["trance", "uplifting", "psy", "psytrance"],
	Techno: ["techno", "minimal", "deep techno"],
	House: ["house", "deep house", "tech house", "future house"],
	Progressive: ["prog", "progressive", "melodic"],
	Dubstep: ["dubstep", "dub", "bass", "riddim"],
	Hardstyle: ["hardstyle", "hard", "hardcore"],
	"Drum & Bass": ["dnb", "drum bass", "d&b", "jungle"],
	Electro: ["electro", "big room", "festival"],
};

/**
 * Parses a natural language search query into structured tokens
 * Examples:
 * - "armin 2025 asot" → artist: "Armin van Buuren", year: 2025, festival: "A State of Trance"
 * - "charlotte tomorrowland techno" → artist: "Charlotte de Witte", festival: "Tomorrowland", genre: "Techno"
 * - "edc 2024 main stage" → festival: "Electric Daisy Carnival", year: 2024, stage: "Main Stage"
 */
export function parseSearchQuery(query: string): ParsedQuery {
	const tokens: SearchToken[] = [];
	const artistHints: string[] = [];
	const festivalHints: string[] = [];
	const yearHints: number[] = [];
	const genreHints: string[] = [];
	const stageHints: string[] = [];

	// Clean and tokenize the query
	const cleanQuery = query.toLowerCase().trim();
	const words = cleanQuery.split(/\s+/);

	// Extract years (2020-2030 range for festivals)
	const yearRegex = /\b(202[0-9]|203[0-9])\b/g;
	const yearMatches = cleanQuery.match(yearRegex);
	if (yearMatches) {
		yearHints.push(...yearMatches.map((y) => Number.parseInt(y, 10)));
		// Add year tokens
		for (const year of yearMatches) {
			tokens.push({
				type: "year",
				value: year,
				confidence: 1.0,
				originalTerm: year,
			});
		}
	}

	// Create a version without years for better matching
	const queryWithoutYears = cleanQuery
		.replace(/\b(202[0-9]|203[0-9])\b/g, "")
		.trim();

	// Check for artist aliases (fuzzy matching for typos)
	for (const [artistName, aliases] of Object.entries(ARTIST_ALIASES)) {
		for (const alias of aliases) {
			// Exact alias match
			if (queryWithoutYears.includes(alias.toLowerCase())) {
				artistHints.push(artistName);
				tokens.push({
					type: "artist",
					value: artistName,
					confidence: 1.0,
					originalTerm: alias,
				});
				break;
			}

			// Fuzzy match for typos (edit distance of 1-2)
			if (alias.length >= 4) {
				const fuzzyMatches = words.some(
					(word) =>
						word.length >= 3 &&
						calculateSimilarity(word, alias.toLowerCase()) > 0.7,
				);
				if (fuzzyMatches) {
					artistHints.push(artistName);
					tokens.push({
						type: "artist",
						value: artistName,
						confidence: 0.8,
						originalTerm: alias,
					});
					break;
				}
			}
		}
	}

	// Check for festival/event aliases
	for (const [festivalName, aliases] of Object.entries(FESTIVAL_ALIASES)) {
		for (const alias of aliases) {
			if (queryWithoutYears.includes(alias.toLowerCase())) {
				festivalHints.push(festivalName);
				tokens.push({
					type: "festival",
					value: festivalName,
					confidence: 1.0,
					originalTerm: alias,
				});
				break;
			}

			// Fuzzy match for festival names
			if (alias.length >= 4) {
				const fuzzyMatches = words.some(
					(word) =>
						word.length >= 3 &&
						calculateSimilarity(word, alias.toLowerCase()) > 0.75,
				);
				if (fuzzyMatches) {
					festivalHints.push(festivalName);
					tokens.push({
						type: "festival",
						value: festivalName,
						confidence: 0.8,
						originalTerm: alias,
					});
					break;
				}
			}
		}
	}

	// Check for stage aliases
	for (const [stageName, aliases] of Object.entries(STAGE_ALIASES)) {
		for (const alias of aliases) {
			if (queryWithoutYears.includes(alias.toLowerCase())) {
				stageHints.push(stageName);
				tokens.push({
					type: "stage",
					value: stageName,
					confidence: 1.0,
					originalTerm: alias,
				});
				break;
			}
		}
	}

	// Check for genre aliases
	for (const [genreName, aliases] of Object.entries(GENRE_ALIASES)) {
		for (const alias of aliases) {
			if (queryWithoutYears.includes(alias.toLowerCase())) {
				genreHints.push(genreName);
				tokens.push({
					type: "genre",
					value: genreName,
					confidence: 1.0,
					originalTerm: alias,
				});
				break;
			}
		}
	}

	// Determine search strategy based on tokens found
	let searchStrategy: "exact" | "partial" | "fuzzy" = "fuzzy";
	const highConfidenceTokens = tokens.filter((t) => t.confidence >= 0.9);

	if (highConfidenceTokens.length >= 2) {
		searchStrategy = "exact"; // We have strong matches, be precise
	} else if (tokens.length >= 1) {
		searchStrategy = "partial"; // Some matches, be moderately flexible
	}

	return {
		tokens,
		artistHints: [...new Set(artistHints)], // Remove duplicates
		festivalHints: [...new Set(festivalHints)],
		yearHints: [...new Set(yearHints)],
		genreHints: [...new Set(genreHints)],
		stageHints: [...new Set(stageHints)],
		originalQuery: query,
		searchStrategy,
	};
}

/**
 * Calculates similarity between two strings using Jaccard similarity
 * Returns a value between 0 and 1, where 1 is identical
 */
function calculateSimilarity(str1: string, str2: string): number {
	const set1 = new Set(str1.toLowerCase().split(""));
	const set2 = new Set(str2.toLowerCase().split(""));

	const intersection = new Set([...set1].filter((x) => set2.has(x)));
	const union = new Set([...set1, ...set2]);

	return intersection.size / union.size;
}

/**
 * Builds optimized search queries for different content types based on parsed query
 */
export function buildSearchQueries(parsedQuery: ParsedQuery) {
	const {
		artistHints,
		festivalHints,
		yearHints,
		genreHints,
		stageHints,
		originalQuery,
	} = parsedQuery;

	return {
		// For artist search: prioritize artist hints but include original query
		artistQuery: artistHints.length > 0 ? artistHints[0] : originalQuery,

		// For festival search: prioritize festival hints
		festivalQuery: festivalHints.length > 0 ? festivalHints[0] : originalQuery,

		// For set search: combine all available hints
		setSearchParams: {
			artistName: artistHints.length > 0 ? artistHints[0] : null,
			festivalName: festivalHints.length > 0 ? festivalHints[0] : null,
			year: yearHints.length > 0 ? yearHints[0] : null,
			genre: genreHints.length > 0 ? genreHints[0] : null,
			stage: stageHints.length > 0 ? stageHints[0] : null,
			fallbackQuery: originalQuery,
		},
	};
}

/**
 * Calculates a relevance score for search results based on how well they match the parsed query
 */
export function calculateRelevanceScore(
	result: {
		artist_name?: string;
		festival_name?: string;
		stage_name?: string;
		start_time?: string;
	},
	parsedQuery: ParsedQuery,
): number {
	let score = 0;
	const { artistHints, festivalHints, yearHints, stageHints } = parsedQuery;

	// Artist name match (highest weight)
	if (result.artist_name && artistHints.length > 0) {
		for (const hint of artistHints) {
			if (result.artist_name.toLowerCase().includes(hint.toLowerCase())) {
				score += 50;
				break;
			}
		}
	}

	// Festival name match (high weight)
	if (result.festival_name && festivalHints.length > 0) {
		for (const hint of festivalHints) {
			if (result.festival_name.toLowerCase().includes(hint.toLowerCase())) {
				score += 40;
				break;
			}
		}
	}

	// Year match (medium weight)
	if (result.start_time && yearHints.length > 0) {
		const resultYear = new Date(result.start_time).getFullYear();
		if (yearHints.includes(resultYear)) {
			score += 30;
		}
	}

	// Stage match (lower weight)
	if (result.stage_name && stageHints.length > 0) {
		for (const hint of stageHints) {
			if (result.stage_name.toLowerCase().includes(hint.toLowerCase())) {
				score += 20;
				break;
			}
		}
	}

	return score;
}
